// Date example
import { DateDecoder } from "@simonbackx/simple-encoding";
import { Formatter } from "@stamhoofd/utility";
import { Filter, FilterDefinition } from "./FilterDefinition";
/**
 * @Example Create a date filter for orders
 * ```ts
 *  const OrderDateDefinition = new FilterDateDefinition<Order>("orderDate", "Besteldatum", (order) => order?.createdAt ?? new Date() )
 * const exampleFilter = new DateFilter<Order>()
 * exampleFilter.definition = OrderDateDefinition
 * exampleFilter.minimumDate = new Date(2020, 0, 1)
 * ```
*/
export class DateFilterDefinition extends FilterDefinition {
    constructor(settings) {
        super(settings);
        /**
         * Also filter on time
         */
        this.time = true;
        if (settings.time !== undefined) {
            this.time = settings.time;
        }
    }
    decode(data) {
        var _a, _b, _c, _d;
        const filter = new DateFilter();
        filter.definition = this;
        // TODO
        filter.mode = (_b = (_a = data.optionalField("mode")) === null || _a === void 0 ? void 0 : _a.enum(DateFilterMode)) !== null && _b !== void 0 ? _b : DateFilterMode.Equal;
        filter.minimumDate = (_c = data.optionalField("minimumDate")) === null || _c === void 0 ? void 0 : _c.decode(DateDecoder);
        filter.maximumDate = (_d = data.optionalField("maximumDate")) === null || _d === void 0 ? void 0 : _d.decode(DateDecoder);
        return filter;
    }
    createFilter() {
        const filter = new DateFilter();
        filter.definition = this;
        return filter;
    }
}
export var DateFilterMode;
(function (DateFilterMode) {
    DateFilterMode["GreaterThan"] = "GreaterThan";
    DateFilterMode["LessThan"] = "LessThan";
    DateFilterMode["Between"] = "Between";
    DateFilterMode["NotBetween"] = "NotBetween";
    DateFilterMode["Equal"] = "Equal";
    DateFilterMode["NotEqual"] = "NotEqual";
})(DateFilterMode || (DateFilterMode = {}));
export class DateFilter extends Filter {
    constructor() {
        super(...arguments);
        this.mode = DateFilterMode.Equal;
    }
    doesMatch(object) {
        const date = new Date(this.definition.getValue(object));
        if (!this.definition.time) {
            date.setHours(0, 0, 0, 0);
        }
        else {
            date.setSeconds(0, 0);
        }
        if (this.mode === DateFilterMode.Between) {
            if (this.minimumDate) {
                if (date < this.minimumDate) {
                    return false;
                }
            }
            if (this.maximumDate) {
                if (date > this.maximumDate) {
                    return false;
                }
            }
        }
        if (this.mode === DateFilterMode.NotBetween) {
            if (this.minimumDate) {
                if (date < this.minimumDate) {
                    return true;
                }
            }
            if (this.maximumDate) {
                if (date > this.maximumDate) {
                    return true;
                }
            }
            return false;
        }
        if (this.mode === DateFilterMode.GreaterThan) {
            if (this.minimumDate) {
                if (date >= this.minimumDate) {
                    return true;
                }
            }
            return false;
        }
        if (this.mode === DateFilterMode.LessThan) {
            if (this.maximumDate) {
                if (date <= this.maximumDate) {
                    return true;
                }
            }
            return false;
        }
        if (this.mode === DateFilterMode.Equal) {
            if (this.minimumDate) {
                if (date.getTime() === this.minimumDate.getTime()) {
                    return true;
                }
            }
            return false;
        }
        if (this.mode === DateFilterMode.NotEqual) {
            if (this.minimumDate) {
                if (date.getTime() === this.minimumDate.getTime()) {
                    return false;
                }
            }
            return true;
        }
        return true;
    }
    encode(context) {
        var _a, _b;
        return {
            definitionId: this.definition.id,
            mode: this.mode,
            minimumDate: (_a = this.minimumDate) === null || _a === void 0 ? void 0 : _a.getTime(),
            maximumDate: (_b = this.maximumDate) === null || _b === void 0 ? void 0 : _b.getTime(),
        };
    }
    get inverted() {
        const filter = this.clone();
        if (filter.mode === DateFilterMode.Between) {
            filter.mode = DateFilterMode.NotBetween;
        }
        else if (filter.mode === DateFilterMode.NotBetween) {
            filter.mode = DateFilterMode.Between;
        }
        else if (filter.mode === DateFilterMode.GreaterThan) {
            filter.mode = DateFilterMode.LessThan;
        }
        else if (filter.mode === DateFilterMode.LessThan) {
            filter.mode = DateFilterMode.GreaterThan;
        }
        else if (filter.mode === DateFilterMode.Equal) {
            filter.mode = DateFilterMode.NotEqual;
        }
        else if (filter.mode === DateFilterMode.NotEqual) {
            filter.mode = DateFilterMode.Equal;
        }
        return filter;
    }
    toString() {
        if (this.definition.explainFilter) {
            return this.definition.explainFilter(this);
        }
        const start = this.minimumDate ? (this.definition.time ? Formatter.dateTime(this.minimumDate) : Formatter.date(this.minimumDate)) : "?";
        const end = this.maximumDate ? (this.definition.time ? Formatter.dateTime(this.maximumDate) : Formatter.date(this.maximumDate)) : "?";
        if (this.mode === DateFilterMode.GreaterThan) {
            return this.definition.name + " is op of na " + start;
        }
        if (this.mode === DateFilterMode.LessThan) {
            return this.definition.name + " is " + end + " of minder";
        }
        if (this.mode === DateFilterMode.Between) {
            return this.definition.name + " tussen " + start + " en " + end;
        }
        if (this.mode === DateFilterMode.NotBetween) {
            return this.definition.name + " niet tussen " + start + " en " + end;
        }
        if (this.mode === DateFilterMode.NotEqual) {
            return this.definition.name + " is niet gelijk aan " + start;
        }
        if (this.mode === DateFilterMode.Equal) {
            return this.definition.name + " is gelijk aan " + start;
        }
        return "Onbekend";
    }
}
//# sourceMappingURL=DateFilter.js.map